import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { restApi } from '../../api/restApiSlice';

const REJECTION_REASONS = ['missing_permissions', 'invalid', 'zone_not_covered', 'unavailable'] as const;

export type CfTokenRejectionReason = (typeof REJECTION_REASONS)[number];

/** Mirrors the server's `TokenValidationResult` (PRE-3281). */
export type CfTokenResult = 'valid' | CfTokenRejectionReason;

type CfTokenVerdict = {
  /** Display names of the permission groups the customer still has to grant. */
  missingPermissions: string[];
  /** Server-side, so a page refresh cannot take the Nexus offer away. */
  attemptNumber: number;
  offerNexus: boolean;
};

export type ConnectCfTokenResponse = CfTokenVerdict & {
  result: 'valid';
  /** The token reaches zones other than this one. A warning, never a refusal. */
  coversOtherZones: boolean;
  lastFour: string | null;
};

/** Undefined attempt fields leave the screen on the count it already had. */
export type CfTokenRejection = Partial<CfTokenVerdict> & {
  reason: CfTokenRejectionReason;
  missingPermissions: string[];
};

export type ConnectCfTokenError = {
  status: FetchBaseQueryError['status'];
  /** Set for a verdict on the token, and for Cloudflare being unable to give one. */
  rejection: CfTokenRejection | null;
};

type ConnectCfTokenBody = Omit<ConnectCfTokenResponse, 'result'> & { result: CfTokenResult };

export const isCfTokenRejectionReason = (value: unknown): value is CfTokenRejectionReason =>
  REJECTION_REASONS.includes(value as CfTokenRejectionReason);

// Field by field, never the body as it came: RTK Query keeps a mutation's error in
// Redux state, and the request this answers carried the token.
const rejectionIn = (data: unknown): CfTokenRejection | null => {
  const body = (data ?? {}) as Partial<Record<keyof CfTokenRejection, unknown>>;
  if (!isCfTokenRejectionReason(body.reason)) return null;

  return {
    reason: body.reason,
    missingPermissions: Array.isArray(body.missingPermissions) ? body.missingPermissions.map(String) : [],
    attemptNumber: typeof body.attemptNumber === 'number' ? body.attemptNumber : undefined,
    offerNexus: typeof body.offerNexus === 'boolean' ? body.offerNexus : undefined,
  };
};

// RTK Query types every endpoint's error as the base query's, so this one's own
// shape has to be cast through it.
const failedWith = (error: ConnectCfTokenError) => ({ error: error as unknown as FetchBaseQueryError });

export type CheckCfTokenRequest = {
  domain: string;
  token: string;
};

export type CheckCfTokenResponse = {
  result: CfTokenResult;
  missingPermissions: string[];
  coversOtherZones: boolean;
};

export type CheckCfTokenFailure = {
  status: FetchBaseQueryError['status'];
  reason: 'beta_closed' | 'invalid' | 'unchecked';
};

const checkFailureReason = (status: FetchBaseQueryError['status']): CheckCfTokenFailure['reason'] => {
  if (status === 403) return 'beta_closed';
  if (status === 400) return 'invalid';
  return 'unchecked';
};

const checkFailedWith = (failure: CheckCfTokenFailure) => ({ error: failure as unknown as FetchBaseQueryError });

export type ConnectedCfToken = {
  domain: string;
  lastFour: string | null;
  connectedAt: string;
  lastUsedAt: string | null;
  deleteAfter: string;
};

export type ConnectedCfTokensResponse = {
  tokens: ConnectedCfToken[];
  total: number;
};

export type ConnectCfTokenRequest = {
  domain: string;
  token: string;
  acknowledgementVersion: string;
};

const cloudflareTokenApi = restApi.injectEndpoints({
  endpoints: (builder) => ({
    connectCfToken: builder.mutation<ConnectCfTokenResponse, ConnectCfTokenRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        const answer = await baseQuery({
          url: '/v3/cloudflare/tokens',
          method: 'POST',
          // The token goes in the body and never in a query parameter: a query
          // parameter is written to access logs.
          body,
        });

        if (answer.error) {
          return failedWith({ status: answer.error.status, rejection: rejectionIn(answer.error.data) });
        }

        const connected = answer.data as ConnectCfTokenBody;
        if (connected.result === 'valid') return { data: connected as ConnectCfTokenResponse };

        // A rest-api older than the 201 contract answers a rejection with 200.
        return failedWith({ status: 200, rejection: rejectionIn({ ...connected, reason: connected.result }) });
      },
    }),
    checkCfToken: builder.mutation<CheckCfTokenResponse, CheckCfTokenRequest>({
      queryFn: async (body, _api, _extraOptions, baseQuery) => {
        const answer = await baseQuery({ url: '/v3/cloudflare/token-checks', method: 'POST', body });

        if (answer.error) {
          const { status } = answer.error;
          return checkFailedWith({ status, reason: checkFailureReason(status) });
        }

        return { data: answer.data as CheckCfTokenResponse };
      },
    }),
    getConnectedCfTokens: builder.query<ConnectedCfTokensResponse, void>({
      query: () => ({
        url: '/v3/cloudflare/tokens',
        method: 'GET',
      }),
      providesTags: ['ConnectedCfTokens'],
    }),
    disconnectCfToken: builder.mutation<void, string>({
      query: (domain) => ({
        url: `/v3/cloudflare/tokens/${encodeURIComponent(domain)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ConnectedCfTokens'],
    }),
  }),
});

export { cloudflareTokenApi };
export const {
  useCheckCfTokenMutation,
  useConnectCfTokenMutation,
  useDisconnectCfTokenMutation,
  useGetConnectedCfTokensQuery,
} = cloudflareTokenApi;
